import _ from 'lodash';
import mapboxgl from 'mapbox-gl';
import React from 'react';
import { MapRef } from 'react-map-gl/mapbox';
import { EventMapItem } from '../types/Event';
import { MapboxOptimizedRoute, MapboxTrip } from '../types/Mapbox';

// Padding values tuned to keep markers visually centered while
// accounting for the left filter panel and extra space on the right.
const MAP_PADDING = {
  top: 80,
  bottom: 80,
  left: 20,
  right: 300,
};

// Larger left/right padding is used when the optimized route panel
// is visible so the route remains fully visible and comfortably centered.
const ROUTE_MAP_PADDING = {
  top: 80,
  bottom: 80,
  left: 320,
  right: 220,
};

const SINGLE_LOCATION_ZOOM = 12;
const MAX_AUTO_ZOOM = 13;
const ROUTE_MAX_AUTO_ZOOM = 11;
const BOUNDS_MARGIN = 0.15;

interface FitLocationsOnMapProps {
  map: MapRef;
  mappedEvents: EventMapItem[];
}

interface FitRouteGeometryOnMapProps {
  map: MapRef;
  geometry?: MapboxTrip['geometry'];
}

/**
 * Expands the provided bounds slightly and fits them into the viewport.
 * This shared helper is used for both event markers and optimized routes.
 */
function fitBoundsWithMargin({ map, bounds, padding, maxZoom }: { map: MapRef; bounds: mapboxgl.LngLatBounds; padding: typeof MAP_PADDING; maxZoom: number }) {
  const lngMargin = (bounds.getEast() - bounds.getWest()) * BOUNDS_MARGIN;
  const latMargin = (bounds.getNorth() - bounds.getSouth()) * BOUNDS_MARGIN;

  const expandedBounds = new mapboxgl.LngLatBounds(
    [bounds.getWest() - lngMargin, bounds.getSouth() - latMargin],
    [bounds.getEast() + lngMargin, bounds.getNorth() + latMargin]
  );

  map.fitBounds(expandedBounds, {
    padding,
    duration: 800,
    maxZoom,
  });
}

/**
 * Fits all event locations into the viewport.
 * Adds a small margin so markers are not positioned against the edges.
 */
export function fitLocationsOnMap({ map, mappedEvents }: FitLocationsOnMapProps) {
  if (_.isEmpty(mappedEvents)) {
    return;
  }

  if (mappedEvents.length === 1) {
    const event = mappedEvents[0];

    map.flyTo({
      center: [event.longitude, event.latitude],
      zoom: SINGLE_LOCATION_ZOOM,
      duration: 800,
    });

    return;
  }

  const bounds = _.reduce(
    mappedEvents,
    (result, event) => result.extend([event.longitude, event.latitude]),
    new mapboxgl.LngLatBounds([mappedEvents[0].longitude, mappedEvents[0].latitude], [mappedEvents[0].longitude, mappedEvents[0].latitude])
  );

  fitBoundsWithMargin({
    map,
    bounds,
    padding: MAP_PADDING,
    maxZoom: MAX_AUTO_ZOOM,
  });
}

/**
 * Fits the optimized route polyline into view.
 * Uses additional left padding to account for the route info panel.
 */
export function fitRouteGeometryOnMap({ map, geometry }: FitRouteGeometryOnMapProps) {
  if (!geometry?.coordinates?.length) {
    return;
  }

  const bounds = geometry.coordinates.reduce(
    (result, coordinate) => result.extend(coordinate as [number, number]),
    new mapboxgl.LngLatBounds(geometry.coordinates[0] as [number, number], geometry.coordinates[0] as [number, number])
  );

  fitBoundsWithMargin({
    map,
    bounds,
    padding: ROUTE_MAP_PADDING,
    maxZoom: ROUTE_MAX_AUTO_ZOOM,
  });
}

// Builds Mapbox coordinate string.
// User location is prepended as the starting point when available.
export function buildOptimizationCoordinates(events: EventMapItem[], coords: GeolocationCoordinates | null) {
  const coordinates = _.map(events, (event) => `${event.longitude},${event.latitude}`);
  if (coords) {
    coordinates.unshift(`${coords.longitude},${coords.latitude}`);
  }
  return coordinates.join(';');
}

// Converts Mapbox optimization response into UI-friendly data.
export function useOptimizedEventRouteData(optimizedRoute: MapboxOptimizedRoute | undefined, mappedEvents: EventMapItem[]) {
  return React.useMemo(() => {
    const trip = optimizedRoute?.trips?.[0];

    // Route geometry used for drawing the polyline.
    const geometry = trip?.geometry;

    // User location is waypoint 0.
    // Event waypoints begin from index 1.
    const waypoints = optimizedRoute?.waypoints ?? [];
    const EVENT_INDEX_OFFSET = 1;

    // Maps original event index to optimized stop number.
    const waypointOrderMap = _.reduce(
      waypoints,
      (result, waypoint, index) => {
        if (waypoint.waypoint_index === 0) {
          return result;
        }
        const eventIndex = waypoint.waypoint_index - EVENT_INDEX_OFFSET;
        result[eventIndex] = index;
        return result;
      },
      {} as Record<number, number>
    );

    // Reorder events to match Mapbox optimized route order.
    const orderedEvents = _.chain(waypoints)
      .filter((waypoint) => waypoint.waypoint_index > 0)
      .map((waypoint) => mappedEvents[waypoint.waypoint_index - EVENT_INDEX_OFFSET])
      .compact()
      .value();

    return {
      trip,
      geometry,
      orderedEvents,
      waypointOrderMap,
    };
  }, [optimizedRoute, mappedEvents]);
}

// Builds a Google Maps search URL for a single address/place query.
export function buildGoogleMapsSearchUrl(query: string): string {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
}

// Builds Google Maps directions URL using optimized stop order.
export function buildGoogleMapsDirectionsUrl(events: EventMapItem[]): string {
  if (events.length < 2) {
    return '';
  }

  const destination = _.last(events)!;

  const waypoints = _.map(_.dropRight(events), (event) => `${event.latitude},${event.longitude}`);

  return `
    https://www.google.com/maps/dir/?api=1
    &destination=${destination.latitude},${destination.longitude}
    &waypoints=${waypoints.join('|')}
    &travelmode=driving
  `.replace(/\s/g, '');
}
